/**
 * copyright-page.tsx
 *
 * Page for generating copyright page BITS XML.
 * Search for a title, preview the generated XML, and download it.
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  searchCopyrightTitles,
  getCopyrightPageXml,
  downloadCopyrightPageXml,
  CopyrightSearchResult,
} from "@/lib/copyright-api";

export default function CopyrightPageGenerator() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTitle, setSelectedTitle] = useState<CopyrightSearchResult | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        setDebouncedSearch(searchTerm.trim());
      } else {
        setDebouncedSearch("");
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search query
  const { data: searchResults, isLoading: searchLoading, error: searchError } = useQuery({
    queryKey: ["copyright-search", debouncedSearch],
    queryFn: () => searchCopyrightTitles(debouncedSearch),
    enabled: debouncedSearch.length >= 2 && !selectedTitle,
  });

  // XML query
  const { data: xmlContent, isLoading: xmlLoading, error: xmlError } = useQuery({
    queryKey: ["copyright-xml", selectedTitle?.titleId],
    queryFn: () => getCopyrightPageXml(selectedTitle!.titleId),
    enabled: !!selectedTitle,
  });

  const handleBack = useCallback(() => {
    setSelectedTitle(null);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selectedTitle) return;
    try {
      await downloadCopyrightPageXml(selectedTitle.titleId, selectedTitle.title);
    } catch (err) {
      console.error("Download failed:", err);
    }
  }, [selectedTitle]);

  return (
    <PageWrapper>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Copyright Page Generator</h1>
          <p className="text-sm text-gray-500 mt-1">
            Search for a title to generate BITS XML for copyright page automation.
          </p>
        </div>

        {selectedTitle ? (
          /* ============ DETAIL VIEW ============ */
          <div>
            {/* Back button */}
            <Button
              variant="ghost"
              size="sm"
              className="mb-4"
              onClick={handleBack}
            >
              <span className="material-icons text-sm mr-1">arrow_back</span>
              Back to search
            </Button>

            {/* Title summary */}
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedTitle.title}</CardTitle>
                    {selectedTitle.subtitle && (
                      <p className="text-sm text-gray-500 mt-1">{selectedTitle.subtitle}</p>
                    )}
                  </div>
                  <Button onClick={handleDownload} disabled={xmlLoading}>
                    <span className="material-icons text-sm mr-1">download</span>
                    Download XML
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {selectedTitle.authorNames && (
                    <div>
                      <span className="font-medium">Author:</span>{" "}
                      {selectedTitle.authorNames}
                    </div>
                  )}
                  {selectedTitle.editorNames && (
                    <div>
                      <span className="font-medium">Editor:</span>{" "}
                      {selectedTitle.editorNames}
                    </div>
                  )}
                  {selectedTitle.publishingHouse && (
                    <div>
                      <span className="font-medium">Publisher:</span>{" "}
                      {selectedTitle.publishingHouse}
                    </div>
                  )}
                  {selectedTitle.copyrightYear && (
                    <div>
                      <span className="font-medium">Year:</span>{" "}
                      {selectedTitle.copyrightYear}
                    </div>
                  )}
                  {selectedTitle.isbnPrint && (
                    <div>
                      <span className="font-medium">ISBN:</span>{" "}
                      {selectedTitle.isbnPrint}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* XML Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">BITS XML Preview</CardTitle>
              </CardHeader>
              <CardContent>
                {xmlLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                  </div>
                ) : xmlError ? (
                  <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-4 text-sm">
                    <span className="font-medium">Failed to generate XML:</span>{" "}
                    {(xmlError as Error).message}
                  </div>
                ) : xmlContent ? (
                  <pre className="bg-gray-50 border rounded-md p-4 text-xs font-mono overflow-x-auto max-h-[600px] overflow-y-auto whitespace-pre-wrap">
                    {xmlContent}
                  </pre>
                ) : (
                  <p className="text-gray-400 text-sm">No XML generated.</p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* ============ SEARCH VIEW ============ */
          <div>
            {/* Search input */}
            <div className="relative mb-6">
              <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                search
              </span>
              <Input
                placeholder="Search by title, ISBN, or Title ID..."
                className="h-12 pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            {/* Search results */}
            {searchLoading && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <Skeleton className="h-5 w-1/2 mb-2" />
                      <Skeleton className="h-4 w-1/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!searchLoading && searchResults && searchResults.length > 0 && (
              <div className="space-y-3">
                {searchResults.map((result) => (
                  <Card
                    key={result.titleId}
                    className="cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                    onClick={() => setSelectedTitle(result)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">
                            {result.title}
                          </h3>
                          {result.subtitle && (
                            <p className="text-sm text-gray-500 truncate">
                              {result.subtitle}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                            {result.authorNames && (
                              <span>{result.authorNames}</span>
                            )}
                            {result.editorNames && (
                              <span>Ed: {result.editorNames}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-4 flex-shrink-0">
                          {result.publishingHouse && (
                            <Badge variant="secondary" className="text-xs">
                              {result.publishingHouse}
                            </Badge>
                          )}
                          {result.copyrightYear && (
                            <span className="text-xs text-gray-400">
                              {result.copyrightYear}
                            </span>
                          )}
                          {result.isbnPrint && (
                            <span className="text-xs text-gray-400 font-mono">
                              {result.isbnPrint}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!searchLoading && searchError && (
              <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-4 text-sm">
                <span className="font-medium">Search failed:</span>{" "}
                {(searchError as Error).message}
              </div>
            )}

            {!searchLoading && !searchError && debouncedSearch && searchResults?.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <span className="material-icons text-4xl mb-2 block">search_off</span>
                <p>No titles found for "{debouncedSearch}"</p>
              </div>
            )}

            {!debouncedSearch && !searchLoading && (
              <div className="text-center py-12 text-gray-400">
                <span className="material-icons text-4xl mb-2 block">description</span>
                <p>Enter a title name, ISBN, or Title ID to search</p>
              </div>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
